/**
 * Staff review queue for patient-reported adverse drug reactions — see
 * what patients report and acknowledge each one once reviewed. Modeled on
 * `StaffRefillReviewPanel`.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, FileDown, Loader2, Send, Plus } from 'lucide-react';
import { FdaExportDialog, downloadFdaForms } from './FdaExportDialog';
import { StaffAdrReportDialog } from './StaffAdrReportDialog';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { useOnlineStatus } from '../lib/useOnlineStatus';
import { OnlineOnlyNotice } from '../components/OnlineOnlyNotice';
import { getPendingAdrReports } from '../lib/offlineDB';
import { addSyncListener } from '../lib/syncService';
import './care.css';

const SEVERITY_BADGE = {
  mild: 'bg-slate-100 text-slate-600',
  moderate: 'bg-amber-100 text-amber-700',
  severe: 'bg-red-100 text-red-700',
};

const STATUS_BADGE = {
  submitted: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-emerald-100 text-emerald-700',
};

export const StaffAdrReviewPanel = () => {
  const [status, setStatus] = useState('submitted');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(null); // report row
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [submitting, setSubmitting] = useState(null); // report being marked submitted
  const [subRef, setSubRef] = useState('');
  const [subDate, setSubDate] = useState('');
  const [savingSub, setSavingSub] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const online = useOnlineStatus();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/staff/portal/adverse-reactions', { params: { status } })
      .then((r) => setRows(r.data.reports || []))
      .catch(() => toast.error('Could not load reaction reports'))
      .finally(() => setLoading(false));
  }, [status]);

  const refreshPendingCount = useCallback(() => {
    getPendingAdrReports().then((rows) => setPendingCount(rows.length)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshPendingCount();
    return addSyncListener((event) => {
      if (event === 'adr_reports_queue_synced') { refreshPendingCount(); load(); }
    });
  }, [refreshPendingCount, load]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    if (!acking) return;
    setSaving(true);
    try {
      await api.post(`/staff/portal/adverse-reactions/${acking.report_id}/acknowledge`, { note: note.trim() });
      toast.success('Report acknowledged');
      setAcking(null); setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to acknowledge');
    } finally { setSaving(false); }
  };

  const markSubmitted = async () => {
    if (!submitting || !subRef.trim()) return;
    setSavingSub(true);
    try {
      await api.post(`/staff/portal/adverse-reactions/${submitting.report_id}/mark-submitted`,
        { reference: subRef.trim(), submitted_date: subDate || undefined });
      toast.success('Marked as submitted to the FDA');
      setSubmitting(null); setSubRef(''); setSubDate('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the submission');
    } finally { setSavingSub(false); }
  };

  const undoSubmitted = async (r) => {
    try {
      await api.post(`/staff/portal/adverse-reactions/${r.report_id}/unmark-submitted`);
      toast.success('Submission cleared');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear the submission'); }
  };

  return (
    <div className="space-y-4" data-testid="staff-adr-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>Reaction reports</h2>
        <div className="flex flex-wrap gap-2">
        <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setFileOpen(true)} data-testid="staff-adr-file-btn">
          <Plus className="w-4 h-4 mr-1" /> File report
        </Button>
        {pendingCount > 0 && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 self-center" data-testid="staff-adr-pending-badge">
            {pendingCount} waiting to sync
          </Badge>
        )}
        <Button variant="outline" disabled={!online} onClick={() => setExportOpen(true)} data-testid="staff-adr-fda-export-btn">
          <FileDown className="w-4 h-4 mr-1" /> Export for Ghana FDA
        </Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="staff-adr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="submitted" data-testid="staff-adr-status-submitted">Awaiting review</SelectItem>
            <SelectItem value="reviewed" data-testid="staff-adr-status-reviewed">Reviewed</SelectItem>
            <SelectItem value="all" data-testid="staff-adr-status-all">All</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>
      <StaffAdrReportDialog open={fileOpen} onOpenChange={setFileOpen} onFiled={() => { refreshPendingCount(); if (navigator.onLine) load(); }} />
      <OnlineOnlyNotice feature="Reaction reports and FDA submissions"
        detail="You are working offline. Reviewing, exporting and recording FDA references resume when you reconnect."
        testId="staff-adr-offline-notice" />
      <FdaExportDialog open={exportOpen} onOpenChange={setExportOpen} onDone={load} />

      {loading && <p className="care-empty" data-testid="staff-adr-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}
      {!loading && rows.length === 0 && <p className="care-empty" data-testid="staff-adr-empty">Nothing in this queue.</p>}

      <div className="space-y-3">
        {rows.map((r) => (
          <article key={r.report_id} className="care-record" data-testid={`staff-adr-${r.report_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex gap-3 items-start min-w-0">
                <div className={`p-2 rounded-lg shrink-0 ${r.severity === 'severe' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate" data-testid={`staff-adr-patient-${r.report_id}`}>
                    {r.patient_name} · {r.medicine_name}{r.batch_number ? ` · batch ${r.batch_number}` : ''}
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.patient_phone && <>{r.patient_phone} · </>}{new Date(r.created_at).toLocaleString()}
                    {r.onset_date && <> · started {r.onset_date}</>}
                    {r.still_taking && <> · still taking it</>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Badge className={SEVERITY_BADGE[r.severity] || SEVERITY_BADGE.mild} data-testid={`staff-adr-severity-${r.report_id}`}>{r.severity}</Badge>
                <Badge className={STATUS_BADGE[r.status] || STATUS_BADGE.submitted} data-testid={`staff-adr-badge-${r.report_id}`}>{r.status}</Badge>
                {r.fda_exported_at && <Badge className="bg-sky-100 text-sky-700" title={`Exported ${new Date(r.fda_exported_at).toLocaleString()}`} data-testid={`staff-adr-fda-exported-${r.report_id}`}>FDA form exported</Badge>}
                {r.fda_submitted_at && <Badge className="bg-indigo-100 text-indigo-700" title={`Submitted ${r.fda_submitted_date || ''} by ${r.fda_submitted_by_name || ''}`} data-testid={`staff-adr-fda-submitted-${r.report_id}`}>Submitted to FDA · {r.fda_reference}</Badge>}
              </div>
            </div>

            <p className="text-sm text-slate-700 mt-2" data-testid={`staff-adr-description-${r.report_id}`}>{r.reaction_description}</p>
            {r.notes && <p className="text-sm text-slate-500 mt-1 italic">"{r.notes}"</p>}
            {r.review_note && (
              <p className="text-sm border-t pt-2 mt-2 whitespace-pre-wrap" data-testid={`staff-adr-review-note-${r.report_id}`}>
                <span className="font-medium">{r.reviewed_by_name}: </span>{r.review_note}
              </p>
            )}

            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" disabled={!online} onClick={async () => { if (await downloadFdaForms({ report_ids: [r.report_id], identity: 'initials' })) load(); }}
                data-testid={`staff-adr-fda-form-${r.report_id}`}>
                <FileDown className="w-4 h-4 mr-1" /> FDA form
              </Button>
              {r.fda_exported_at && !r.fda_submitted_at && (
                <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" disabled={!online}
                  onClick={() => { setSubmitting(r); setSubRef(''); setSubDate(new Date().toISOString().slice(0, 10)); }}
                  data-testid={`staff-adr-mark-submitted-${r.report_id}`}>
                  <Send className="w-4 h-4 mr-1" /> Mark submitted
                </Button>
              )}
              {r.fda_submitted_at && (
                <Button size="sm" variant="ghost" className="text-slate-500" disabled={!online}
                  onClick={() => undoSubmitted(r)} data-testid={`staff-adr-undo-submitted-${r.report_id}`}>
                  Undo submission
                </Button>
              )}
            {r.status === 'submitted' && (
                <Button
                  size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!online}
                  onClick={() => { setAcking(r); setNote(''); }} data-testid={`staff-adr-acknowledge-${r.report_id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Acknowledge
                </Button>
            )}
            </div>
          </article>
        ))}
      </div>

      <Dialog open={!!acking} onOpenChange={(o) => { if (!o) { setAcking(null); setNote(''); } }}>
        <DialogContent data-testid="staff-adr-acknowledge-dialog" closeProps={{ 'data-testid': 'staff-adr-acknowledge-close' }}>
          <DialogHeader>
            <DialogTitle>Acknowledge reaction report</DialogTitle>
            <DialogDescription>Add an optional clinical note — this marks the report as reviewed.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note…" data-testid="staff-adr-acknowledge-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAcking(null); setNote(''); }}>Cancel</Button>
            <Button onClick={acknowledge} disabled={saving || !online} data-testid="staff-adr-acknowledge-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!submitting} onOpenChange={(o) => { if (!o) { setSubmitting(null); setSubRef(''); setSubDate(''); } }}>
        <DialogContent data-testid="staff-adr-submit-dialog">
          <DialogHeader>
            <DialogTitle>Record FDA submission</DialogTitle>
            <DialogDescription>Keep the reference number the Food and Drugs Authority gave you for this reaction report.</DialogDescription>
          </DialogHeader>
          <OnlineOnlyNotice feature="Recording an FDA submission" testId="staff-adr-submit-offline-notice" />
          <div className="space-y-3">
            <div>
              <Label htmlFor="adr-fda-ref">FDA reference number</Label>
              <Input id="adr-fda-ref" value={subRef} onChange={(e) => setSubRef(e.target.value)}
                placeholder="e.g. FDA/SMD/ADR/2026/0142" data-testid="staff-adr-submit-ref-input" />
            </div>
            <div>
              <Label htmlFor="adr-fda-date">Date submitted</Label>
              <Input id="adr-fda-date" type="date" value={subDate} onChange={(e) => setSubDate(e.target.value)}
                data-testid="staff-adr-submit-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSubmitting(null); setSubRef(''); setSubDate(''); }}>Cancel</Button>
            <Button onClick={markSubmitted} disabled={savingSub || !subRef.trim() || !online} data-testid="staff-adr-submit-confirm">
              {savingSub ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
